/**
 * Shared task-freshness helper. Renders a compact green→amber→red pill
 * driven by the same threshold rules the backend Unified Inbox uses:
 *
 *   • no due date / completed / >2 days out  → green
 *   • due today or tomorrow                  → amber
 *   • past due                               → red
 */
import { AlertCircle, Clock, CheckCircle2 } from "lucide-react";

function daysUntil(dueDate) {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
}

export function taskLight(status, dueDate) {
  if (status === "completed") return "green";
  const d = daysUntil(dueDate);
  if (d === null) return "grey";
  if (d < 0) return "red";
  if (d <= 1) return "amber";
  return "green";
}

const STYLE = {
  red:   { cls: "bg-red-100 text-red-800 border-red-300",           label: "Overdue",     Icon: AlertCircle },
  amber: { cls: "bg-amber-100 text-amber-800 border-amber-300",     label: "Due soon",    Icon: Clock },
  green: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "On track",   Icon: CheckCircle2 },
  grey:  { cls: "bg-zinc-100 text-zinc-500 border-zinc-200",         label: "No due date", Icon: Clock },
};

export function TaskFreshnessPill({ status, dueDate, className = "" }) {
  const light = taskLight(status, dueDate);
  const { cls, label, Icon } = STYLE[light];
  const days = daysUntil(dueDate);
  const tooltip =
    light === "red"   ? `Past due by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}` :
    light === "amber" ? (days === 0 ? "Due today" : "Due tomorrow") :
    light === "green" && days !== null ? `Due in ${days} day${days === 1 ? "" : "s"}` :
    label;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${cls} ${className}`}
      title={tooltip}
      data-testid={`task-freshness-${light}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
